import React, { FC, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSize, fontSizeAndHeight, FontSizeKeys } from '../../../styleHelpers/fontSizes';
import { modalsShadow } from '../../../styleHelpers/mixins/shadow';
import { EToolTipTheme, ETooltipArrowDirection, ToolTip } from '../ToolTip/ToolTip';
import { calculateTooltipPosition } from '../ToolTip/tooltipPositionUtils';

const Wrapper = styled.div`
    position: relative;
    > div {
        display: none;
        position: absolute;
        padding: 10px;
        border-radius: 4px;
        font-size: ${fontSize[16]};
        ${modalsShadow()};
        background: ${colorStack.white};
        color: ${colorStack.content};
    }
    &:hover {
        > div {
            display: block;
            z-index: 99;
        }
    }
`;

const Text = styled.span<{ numberOfLines: number, fontSize: number }>`
    width: 100%;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: nowrap;
    ${props => props.fontSize && css`
        ${fontSizeAndHeight[props.fontSize]};
    `}
    ${props => props.numberOfLines > 1 && css`
        display: -webkit-box !important;
        white-space: normal;
        -webkit-line-clamp: ${props.numberOfLines};
    `}
`;

interface IProps {
    numberOfLines: number;
    noTooltip?: boolean;
    fontSize?: FontSizeKeys;
    className?: string;
    tooltipTheme?: EToolTipTheme;
    autoWidth?: boolean;
    onShowEllipsis?: () => void;
    onHideEllipsis?: () => void;
}

export const MultilineTextWithEllipsis: FC<React.PropsWithChildren<IProps>> = props => {
    const textRef = useRef<HTMLDivElement>();
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const tooltipWidthRef = useRef<number>(0);
    const tooltipHeightRef = useRef<number>(0);
    const [coords, setCoords] = useState<[number, number]>([0, 0]);
    const [arrowDirection, setArrowDirection] = useState(ETooltipArrowDirection.TOP);

    const hasEllipsis = () => {
        const element = textRef?.current;
        const lineHeightOffset = 1.1;
        const isTextClamped = element.scrollHeight > element.clientHeight + lineHeightOffset;
        const isTextTruncated = element.scrollWidth > element.clientWidth;
        return isTextClamped || isTextTruncated;
    };

    const updateTooltipPosition = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!tooltipWidthRef.current || !tooltipHeightRef.current) return;

        const rect = textRef.current?.getBoundingClientRect();
        const position = calculateTooltipPosition({
            cursorX: rect ? rect.left + rect.width / 2 : e.clientX,
            cursorY: e.clientY,
            tooltipWidth: tooltipWidthRef.current,
            tooltipHeight: tooltipHeightRef.current
        });

        setCoords([position.left, position.top]);
        setArrowDirection(position.arrowDirection);
    };

    const recalculatePositionFromElement = (width?: number, height?: number) => {
        if (width !== undefined) tooltipWidthRef.current = width;
        if (height !== undefined) tooltipHeightRef.current = height;
        const currentWidth = tooltipWidthRef.current;
        const currentHeight = tooltipHeightRef.current;

        if (currentWidth > 0 && currentHeight > 0 && textRef.current && showTooltip) {
            const rect = textRef.current.getBoundingClientRect();
            const position = calculateTooltipPosition({
                cursorX: rect.left + rect.width / 2,
                cursorY: rect.top + rect.height / 2,
                tooltipWidth: currentWidth,
                tooltipHeight: currentHeight
            });
            setCoords([position.left, position.top]);
            setArrowDirection(position.arrowDirection);
        }
    };

    const showTooltipHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        if (tooltipWidthRef.current > 0 && tooltipHeightRef.current > 0) {
            updateTooltipPosition(e);
        }
        setShowTooltip(hasEllipsis());
    };

    const ellipsisChangeHandler = () => {
        if (hasEllipsis()) {
            props.onShowEllipsis?.();
        } else {
            props.onHideEllipsis?.();
        }
    };

    const mouseMoveOrOverHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        if (props.noTooltip) {
            return (props.onShowEllipsis || props.onHideEllipsis) && ellipsisChangeHandler();
        }
        if (showTooltip && tooltipWidthRef.current > 0) {
            updateTooltipPosition(e);
        }
        return showTooltipHandler(e);
    };

    const noMouseInteractivity = useMemo(() => {
        return props.noTooltip && !props.onShowEllipsis && !props.onShowEllipsis;
    }, [props.noTooltip, props.onShowEllipsis, props.onHideEllipsis]);

    return (
        <Wrapper className={props.className}>
            <Text
                numberOfLines={props.numberOfLines}
                ref={textRef}
                fontSize={props.fontSize}
                onMouseMove={noMouseInteractivity ? undefined : mouseMoveOrOverHandler}
                onMouseOver={noMouseInteractivity ? undefined : mouseMoveOrOverHandler}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {props.children}
            </Text>
            {showTooltip &&
                <ToolTip
                    title={`${props.children}`}
                    left={coords[0]}
                    top={coords[1]}
                    setTooltipWidth={(width) => {
                        recalculatePositionFromElement(width, undefined);
                    }}
                    setTooltipHeight={(height) => {
                        recalculatePositionFromElement(undefined, height);
                    }}
                    fixedWidth={props.autoWidth ? undefined : 496}
                    reportRenderedWidth
                    theme={props.tooltipTheme}
                    arrowDirection={arrowDirection}
                />
            }
        </Wrapper>
    );
};
